import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Dialog, DialogContent, Button, Typography, Box, CircularProgress, Paper } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import SearchHeader from "./SearchHeader";
import ActiveCandidates from "./CandidatesView/ActiveCandidates";
import JobDetailView from "./JobDetailView";
import RecruiterView from "./RecruiterView";
import CandidatesTabs from "./CandidatesTabs";
import DocumentsView from "./CandidateInfo/DocumentsView";
import ResumesView from "./CandidateInfo/ResumesView";
import ProfileView from "./CandidateInfo/ProfileView";
import { myCandidatesSelector } from "./redux/selector";
import { appSelector } from "../redux/selector";
import { selectedTabViewAction, setActiveCandidateTab, selectedSubmissionAction, clearSubmissionViewStateAction } from "./redux/action";
import SubmissionFlow from "./CandidatesTabs/Submissions/SubmissionFlow";
import ResumeSelectionView from "./ResumeSelection/ResumeSelectionView";
import Nocandidate from "../images/Nocandidate.png";
import CandidateJobFilters from "../MyCandidates/CandidateJobFilters";
import { userSelector } from "../user-redux/selector";
import { useLocation } from "react-router-dom";

const MyCandidates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { tabView, activeCandidates, activeCandidatesLoading } = useSelector(myCandidatesSelector);
  const { activeCandLoading } = useSelector(appSelector);
  const { user } = useSelector(userSelector);

  const [openView, setOpenView] = useState();
  const [searchHasNoResults, setSearchHasNoResults] = useState(false);
  const [filters, setFilters] = useState({
    postedDate: [],
    workType: [],
    jobType: []
  });

  // Effect to select Submission tab if returning from Zoho Oauth
  useEffect(() => {
    const hasZoho = localStorage.getItem("zoho_token");
    if (hasZoho) {
      dispatch(selectedTabViewAction("Submission"));
      localStorage.removeItem("zoho_token");
    }
  }, [dispatch]);

  // Reset sticky submission state when opening My Candidates without an intentional deep-link
  useEffect(() => {
    const isDeepLink =
      location.state?.tabView === "Submission" ||
      location.state?.candidateId ||
      !!sessionStorage.getItem("target_candidate_id") ||
      !!sessionStorage.getItem("zoho_redirect") ||
      !!sessionStorage.getItem("zoho_redirect_jobs");

    if (!isDeepLink) {
      dispatch(clearSubmissionViewStateAction());
    } else {
      // Deep-link: keep target candidate/tab, but clear old submission payload
      dispatch(selectedSubmissionAction(null));
    }

    return () => {
      // Leaving My Candidates — don't leave Submissions tab/selection sticky
      dispatch(selectedSubmissionAction(null));
      sessionStorage.removeItem("target_job_id");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to watch for filter changes
  useEffect(() => {
    if (filters.postedDate.length > 0) {
      // Get the most recent date filter
      const lastFilter = filters.postedDate[filters.postedDate.length - 1];
      // Any components needing this filter will already be listening via Redux
      // Or other state management mechanisms
    }
  }, [filters.postedDate]);

  const showNoCandidateDialog =
    !activeCandLoading &&
    !activeCandidatesLoading &&
    Array.isArray(activeCandidates) &&
    activeCandidates.length === 0;

  const handleCloseDialog = () => {
    if (user?.usertype == 7 || user?.usertype == 13) {
      navigate("/admin-dashboard", { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  const openProfileView = (selected) => {
    dispatch(selectedTabViewAction(selected));
    //dispatch(setActiveCandidateTab(0));
    setOpenView(selected);
  };

  const renderRightPanels = () => {
    if (searchHasNoResults) {
      return (
        <Grid item xs={9.4} sx={{ height: "100%" }}>
          <Paper
            elevation={2}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              No candidates available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No candidates match your search. Try a different name.
            </Typography>
          </Paper>
        </Grid>
      );
    }

    if (tabView === "Resume Selection") {
      return (
        <Grid item xs={9.4} sx={{ height: '100%' }}>
          <ResumeSelectionView />
        </Grid>
      );
    }

    if (tabView === "Resume View") {
      return (
        <Grid item xs={9.4} sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ResumesView />
        </Grid>
      );
    }

    if (tabView === "Documents") {
      return (
        <Grid item xs={9.4} sx={{ height: '100%' }}>
          <DocumentsView />
        </Grid>
      );
    }

    if (tabView === "Profile View") {
      return (
        <Grid item xs={9.4} sx={{ height: '100%' }}>
          <ProfileView />
        </Grid>
      );
    }

    return (
      <>
        <Grid item sm={2.6} sx={{ height: '100%' }}>
          <CandidatesTabs />
        </Grid>
        {tabView === "Submission" ? (
          <>
            <Grid item sm={4.4} sx={{ height: '100%' }}>
              <SubmissionFlow />
            </Grid>
            <Grid item sm={2.4} sx={{ height: '100%' }}>
              <RecruiterView />
            </Grid>
          </>
        ) : (
          <>
            <Grid item sm={4.4} sx={{ height: '100%' }}>
              <JobDetailView />
            </Grid>
            <Grid item sm={2.4} sx={{ height: '100%' }}>
              <RecruiterView />
            </Grid>
          </>
        )}
      </>
    );
  };

  return (
    <div style={{
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      marginLeft: '-5px',
      marginRight: '-5px',
      width: 'calc(100% + 10px)'
    }}>
      {/* Loading Screen */}
      {activeCandidatesLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            gap: 2
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" color="textSecondary">
            Loading candidates...
          </Typography>
        </Box>
      ) : (
        <>
          <Dialog
            open={showNoCandidateDialog}
            sx={{ "& .MuiDialog-paper": { width: "400px" } }}
            fullWidth
            maxWidth="xs"
          >
            <DialogContent sx={{ textAlign: "center", padding: "15px" }}>
              <img
                src={Nocandidate}
                alt="No Candidates"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "cover",
                  borderRadius: "8px",
                  marginBottom: "16px",
                }}
              />
              <Typography variant="body1" sx={{ padding: "5px" }}>
                No Candidate data was found based on the current search criteria.
              </Typography>
              <Box mt={2} display="flex" justifyContent="center" gap={2}>
                <Button onClick={handleCloseDialog} color="primary" variant="outlined">
                  Close
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          {!showNoCandidateDialog && (
          <>
          <Box
            sx={{
              zIndex: 1000,
              pt: 1,
              pb: 0,
              flexShrink: 0
            }}
          >
            <SearchHeader />
          </Box>

          <Box sx={{ flex: 1, overflow: 'hidden', pt: 1 }}>
            <Grid container spacing={1} sx={{ height: '100%' }}>
              <Grid item sm={2.6} sx={{ height: '100%' }}>
                <ActiveCandidates
                  openProfileView={openProfileView}
                  onSearchEmptyChange={setSearchHasNoResults}
                />
              </Grid>

              {renderRightPanels()}
            </Grid>
          </Box>
          </>
          )}
        </>
      )}
    </div>
  );
};

export default MyCandidates;
